/* Notification Center — full history with search + filters + archive/delete/mark-read. */

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Bell, CheckCheck, Archive, Trash2, ExternalLink, Search, ArrowLeft,
  FileCheck, AlertTriangle, CreditCard, Info,
} from "lucide-react";
import { useNotifications } from "../hooks/useNotifications";
import "./NotificationCenter.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconFor = (n) => {
  const map = { "file-check": FileCheck, "alert-triangle": AlertTriangle, "credit-card": CreditCard, "info": Info };
  return map[n.icon] || (n.kind === "billing" ? CreditCard : n.kind === "system" ? Info : FileCheck);
};

const KIND_LABEL = { report: "Reports", billing: "Billing", account: "Account", system: "System" };

const relTime = (iso) => {
  if (!iso) return "";
  const secs = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

export default function NotificationCenter() {
  const nav = useNavigate();
  const { markRead, markAllRead, archive, remove, load: refreshCtx } = useNotifications();
  const [items, setItems] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("");
  const [readState, setReadState] = useState(""); // ""|"unread"|"read"
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "200" });
      if (kind) params.append("kind", kind);
      if (readState === "unread") params.append("unread", "true");
      if (readState === "read") params.append("unread", "false");
      if (showArchived) params.append("archived", "true");
      const r = await axios.get(`${API}/notifications?${params}`);
      setItems(r.data.notifications || []);
    } catch {
      setItems([]);
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [kind, readState, showArchived]);

  const filtered = useMemo(() => {
    if (!q) return items;
    const s = q.toLowerCase();
    return items.filter((n) => (n.title || "").toLowerCase().includes(s) ||
                               (n.body || "").toLowerCase().includes(s));
  }, [items, q]);

  const onMarkRead = async (n) => {
    setItems((p) => p.map((x) => x.id === n.id ? { ...x, read_at: x.read_at || new Date().toISOString() } : x));
    await markRead(n.id);
  };
  const onMarkAll = async () => {
    setItems((p) => p.map((x) => ({ ...x, read_at: x.read_at || new Date().toISOString() })));
    await markAllRead();
  };
  const onArchive = async (n) => {
    if (!showArchived) setItems((p) => p.filter((x) => x.id !== n.id));
    await archive(n.id);
    refreshCtx();
  };
  const onDelete = async (n) => {
    if (!window.confirm("Delete this notification?")) return;
    setItems((p) => p.filter((x) => x.id !== n.id));
    await remove(n.id);
  };
  const onOpen = async (n) => {
    if (!n.read_at) await onMarkRead(n);
    if (n.action_url) nav(n.action_url);
  };

  const anyUnread = filtered.some((n) => !n.read_at);

  return (
    <div className="ncp" data-testid="notification-center">
      <header className="ncp-top">
        <div className="ncp-top-l">
          <button className="ncp-back" onClick={() => nav(-1)} data-testid="nc-back">
            <ArrowLeft size={13} /> Back
          </button>
          <div>
            <h1 className="ncp-title"><Bell size={22} /> Notifications</h1>
            <p className="ncp-sub">Every alert across your Liturgical Intelligence™ workspace.</p>
          </div>
        </div>
        <div className="ncp-actions">
          <button className="ncp-btn" onClick={onMarkAll} disabled={!anyUnread} data-testid="nc-mark-all">
            <CheckCheck size={13} /> Mark all read
          </button>
          <button
            className={`ncp-btn ${showArchived ? "on" : ""}`}
            onClick={() => setShowArchived((v) => !v)}
            data-testid="nc-toggle-archived"
          >
            <Archive size={13} /> {showArchived ? "Viewing archived" : "Show archived"}
          </button>
        </div>
      </header>

      <div className="ncp-filters">
        <div className="ncp-search">
          <Search size={14} />
          <input
            placeholder="Search notifications…"
            value={q} onChange={(e) => setQ(e.target.value)}
            data-testid="nc-search"
          />
        </div>
        <div className="ncp-select">
          <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="nc-filter-kind">
            <option value="">All kinds</option>
            <option value="report">Reports</option>
            <option value="billing">Billing</option>
            <option value="account">Account</option>
            <option value="system">System</option>
          </select>
        </div>
        <div className="ncp-select">
          <select value={readState} onChange={(e) => setReadState(e.target.value)} data-testid="nc-filter-read">
            <option value="">Read & unread</option>
            <option value="unread">Unread only</option>
            <option value="read">Read only</option>
          </select>
        </div>
      </div>

      <div className="ncp-count" data-testid="nc-count">
        {loading ? "Loading…" : `${filtered.length} notification${filtered.length === 1 ? "" : "s"}`}
      </div>

      {!loading && filtered.length === 0 ? (
        <div className="ncp-empty" data-testid="nc-empty">
          <Bell size={40} />
          <h3>Nothing here</h3>
          <p>You&#39;ll see report completions, billing events, and account activity here as they happen.</p>
        </div>
      ) : (
        <ul className="ncp-list" data-testid="nc-list">
          {filtered.map((n) => {
            const Icon = iconFor(n);
            return (
              <li
                key={n.id}
                className={`ncp-item p-${n.priority} ${n.read_at ? "read" : "unread"}`}
                data-testid={`nc-item-${n.id}`}
              >
                <span className="ncp-i"><Icon size={16} /></span>
                <div className="ncp-body" onClick={() => onOpen(n)}>
                  <div className="ncp-titleline">
                    <b>{n.title}</b>
                    <span className="ncp-kind">{KIND_LABEL[n.kind] || n.kind}</span>
                  </div>
                  {n.body && <div className="ncp-blurb">{n.body}</div>}
                  <div className="ncp-meta">
                    <span>{relTime(n.created_at)}</span>
                    {n.action_url && (
                      <span className="ncp-open">
                        <ExternalLink size={11} /> Open
                      </span>
                    )}
                  </div>
                </div>
                <div className="ncp-item-actions">
                  {!n.read_at && (
                    <button onClick={() => onMarkRead(n)} title="Mark as read" data-testid={`nc-read-${n.id}`}>
                      <CheckCheck size={13} />
                    </button>
                  )}
                  {!n.archived_at && (
                    <button onClick={() => onArchive(n)} title="Archive" data-testid={`nc-archive-${n.id}`}>
                      <Archive size={13} />
                    </button>
                  )}
                  <button className="danger" onClick={() => onDelete(n)} title="Delete" data-testid={`nc-delete-${n.id}`}>
                    <Trash2 size={13} />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
